import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useSelector } from "react-redux";
import { dispatch } from "../../state/store";
import {
  pushCreatedTableRowsIds,
  resetConsentElements,
  setAppsRefresh,
  setBreadcrumbsRedirectUrl,
  setConsentElements,
  setConsentElementsError,
  setConsentElementsVisibility,
  setConsentsLoading,
  setConsentsRefresh,
  setIsDuplicate,
  setIsViewOnly,
  setSelectedConsent
} from "../../state/ducks";

import { alignItems, consentStatuses, routePaths, strCode, tableDataStatuses } from "../../consts";
import { consentsService, log, notificationService, t } from "../../services";
import { consentUtil } from "../../utils";
import { withSection } from "../../hoc";

import { ChangesNotSavedModal, ConsentElementsTable } from "../../containers";
import {
  AppManagementBreadcrumbs,
  FlexContainers,
  Heading5,
  IconButton,
  Modal,
  PageContainer,
  Paragraph2,
  SmallButton,
  StatusIndicator,
  SVG,
  WrapperContainer
} from "../../components";

const setIsConsentVisible = (consentElements) => {
  return consentElements.map((item) => {
    return {
      ...item,
      isConsentVisible: item.isConsentVisible
        ? item.isConsentVisible
        : Boolean(item.groupName && item.type && item.code && item.texts)
    };
  });
};

const ConsentElementsPage = () => {
  const [version, setVersion] = useState();
  const [status, setStatus] = useState();
  const [isNew, setIsNew] = useState(false);

  const [isSameData, setIsSameData] = useState(true);
  const [activateModalActive, setActivateModalActive] = useState(false);
  const [isChangesNotSavedModalActive, setIsChangesNotSavedModalActive] = useState(false);

  const breadcrumbsRedirectUrl = useSelector((store) => store.global?.breadcrumbsRedirectUrl);

  const loading = useSelector((store) => store.consents?.loading);
  const error = useSelector((store) => store.consents?.errors?.consentElements);

  const selectedConsent = useSelector((store) => store.consents.selectedConsent);
  const consentElements = useSelector((store) => store.consents.consentElements);
  const isDuplicate = useSelector((store) => store.consents.isDuplicate);

  const market = useSelector((store) => store.consents.market);
  const appType = useSelector((store) => store.consents.appType);

  const { appName } = useParams();

  const isViewOnly = useMemo(
    () => status !== consentStatuses.draft && !isNew && !isDuplicate,
    [status, isNew, isDuplicate]
  );

  const { id } = useParams();
  const history = useHistory();

  const fetchConsent = useCallback(
    async (id) => {
      dispatch(setConsentElementsError(null));

      if (selectedConsent !== null || loading) {
        return;
      }

      dispatch(setConsentsLoading(true));

      try {
        dispatch(setSelectedConsent(null));
        dispatch(resetConsentElements());

        const res = await consentsService.fetchConsent(id);

        const selectedConsentData = { ...res };
        selectedConsentData.items = consentUtil.setConsentElementsIds(selectedConsentData.items);

        let isVisible;

        if (selectedConsentData?.items?.length) {
          const consentElement = selectedConsentData.items[0];

          isVisible =
            consentElement?.groupName &&
            consentElement?.type &&
            consentElement?.code &&
            consentElement?.texts;
        }

        dispatch(setSelectedConsent(selectedConsentData));
        dispatch(setConsentElements(selectedConsentData?.items ?? []));
        dispatch(setConsentElementsVisibility(isVisible));
      } catch (err) {
        dispatch(setConsentElementsError(err));

        log.error("ConsentElementsPage: Failed to fetch consent", err);

        notificationService.showError({
          title: t(strCode.FETCH_CONSENT_ERROR),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });
      } finally {
        dispatch(setConsentsLoading(false));
      }
    },
    [id]
  );

  const publishNewConsent = useCallback(async () => {
    dispatch(setConsentsLoading(true));

    try {
      const consentConfig = {
        market,
        appType,
        id: null
      };

      const res = await consentsService.publishNewConsent(consentConfig);

      return res?.id;
    } catch (err) {
      log.error("ConsentElementsPage: Failed to publish consent", err);

      notificationService.showError({
        title: t(strCode.PUBLISH_CONSENT_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });

      throw err;
    } finally {
      dispatch(setConsentsLoading(false));
    }
  }, [market, appType]);

  const updateConsentElements = useCallback(
    async (id) => {
      dispatch(setConsentsLoading(true));

      try {
        const consent = {
          id,
          items: setIsConsentVisible(consentUtil.removeConsentElementsIds(consentElements))
        };

        await consentsService.updateConsentElements(consent);

        return id;
      } catch (err) {
        log.error("ConsentElementsPage: Failed to update consent", err);

        notificationService.showError({
          title: t(strCode.UPDATE_CONSENT_ERROR),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });

        throw err;
      } finally {
        dispatch(setConsentsLoading(false));
      }
    },
    [id, consentElements]
  );

  const activateConsent = useCallback(
    async (id) => {
      dispatch(setConsentsLoading(true));

      try {
        const consentConfig = {
          market,
          appType,
          id
        };

        return await consentsService.activateConsent(consentConfig);
      } catch (err) {
        log.error("ConsentElementsPage: Failed to activate consent", err);

        notificationService.showError({
          title: t(strCode.ACTIVATE_CONSENT_ERROR),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });

        throw err;
      } finally {
        dispatch(setConsentsLoading(false));
      }
    },
    [id, market, appType]
  );

  useEffect(() => {
    dispatch(setIsViewOnly(isViewOnly));
  }, [isViewOnly]);

  useEffect(() => {
    if (selectedConsent) {
      setStatus(selectedConsent?.status);
      setVersion(selectedConsent?.version);
    } else {
      if (id !== undefined) {
        fetchConsent(id);
      } else {
        setVersion(consentUtil.getNewConsentVersion());
        setIsNew(true);

        dispatch(setConsentElementsError(null));
      }
    }
  }, [id, selectedConsent]);

  useEffect(() => {
    let isSame = true;

    if (consentElements?.length && selectedConsent) {
      isSame = consentUtil.checkIsSameConsentElements(selectedConsent?.items, consentElements);
    }

    setIsSameData(isSame);
  }, [selectedConsent?.leaf, consentElements]);

  const consentVersionTitle = useMemo(() => {
    return isViewOnly
      ? t(strCode.CONSENT_VERSION_TITLE, { version: version ?? "" })
      : isDuplicate
      ? t(strCode.DUPLICATE_CONSENT_VERSION_TITLE, { version: version ?? "" })
      : t(strCode.NEW_CONSENT_VERSION_TITLE, { version: version ?? "" });
  }, [isViewOnly, isDuplicate, version]);

  const onUpdatePositions = (newConsentElements) => {
    if (newConsentElements) {
      const data = newConsentElements.map((element, index) => {
        return { ...element, position: index };
      });

      dispatch(setConsentElements(data));
    }
  };

  const resetConsentsData = () => {
    dispatch(setSelectedConsent(null));
    dispatch(resetConsentElements());
    dispatch(setIsDuplicate(false));
    dispatch(setIsViewOnly(false));
  };

  const redirectToBreadcrumbsUrlOrDefault = () => {
    if (breadcrumbsRedirectUrl) {
      if (breadcrumbsRedirectUrl === routePaths.APPS) {
        dispatch(setAppsRefresh(true));
      }

      dispatch(setBreadcrumbsRedirectUrl(""));
      history.push(breadcrumbsRedirectUrl);
    } else {
      history.push(`${routePaths.APPS}/${appName}${routePaths.CONSENTS}`);
    }
  };

  const onClose = () => {
    redirectToBreadcrumbsUrlOrDefault();
    resetConsentsData();
  };

  const onBack = () => {
    if (
      (selectedConsent && consentElements.length && !isSameData) ||
      (!selectedConsent && consentElements.length)
    ) {
      setIsChangesNotSavedModalActive(true);
    } else {
      onClose();
    }
  };

  const onUndoChanges = () => {
    if (selectedConsent) {
      dispatch(setConsentElements(selectedConsent?.items));
    }
  };

  const onSaveDraft = async () => {
    setIsSameData(true);
    setActivateModalActive(false);

    try {
      if (isDuplicate || isNew || isViewOnly) {
        // if isDuplicate or isNew or isViewOnly -> publishNewConsent -> updateConsentElements
        const newId = await publishNewConsent();
        await updateConsentElements(newId);

        dispatch(pushCreatedTableRowsIds([newId]));
      } else {
        // if editing an existing draft consent -> updateConsentElements
        await updateConsentElements(id);
      }

      dispatch(setConsentsRefresh(true));

      notificationService.showSuccess({
        title: t(strCode.UPDATE_CONSENT_SUCCESS),
        message: t(strCode.CONSENT_UPDATE_SUCCESS)
      });

      redirectToBreadcrumbsUrlOrDefault();
      resetConsentsData();
    } catch (err) {
      log.error(`An error occurred while saving consent data.`, err);
    }
  };

  const onSaveAndActivate = async () => {
    setIsSameData(true);
    setActivateModalActive(false);

    let activatedVersion = version;

    try {
      if (isDuplicate || isNew) {
        // if isDuplicate or isNew -> publishNewConsent -> updateConsentElements -> activateConsent
        const newId = await publishNewConsent();
        const updatedId = await updateConsentElements(newId);
        const res = await activateConsent(updatedId);

        dispatch(pushCreatedTableRowsIds([newId]));

        activatedVersion = res?.version ?? version;
      } else {
        // if editing an existing draft consent -> updateConsentElements -> activateConsent
        const updatedId = await updateConsentElements(id);
        const res = await activateConsent(updatedId);

        activatedVersion = res?.version ?? version;

        dispatch(pushCreatedTableRowsIds([updatedId]));
      }

      dispatch(setConsentsRefresh(true));

      notificationService.showSuccess({
        title: t(strCode.ACTIVATE_CONSENT_SUCCESS),
        message: t(strCode.CONSENT_ACTIVATE_SUCCESS, { version: activatedVersion })
      });

      redirectToBreadcrumbsUrlOrDefault();
      resetConsentsData();
    } catch (err) {
      log.error(`An error occurred while saving and activating consent data.`, err);
    }
  };

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%"
        height="100%">
        <AppManagementBreadcrumbs openConsentsModal={() => setIsChangesNotSavedModalActive(true)} />

        <WrapperContainer Container={FlexContainers.FlexRowBetween} width="100%">
          <WrapperContainer Container={FlexContainers.FlexRowCenter} gap={20}>
            <Heading5>{consentVersionTitle}</Heading5>

            {status && isViewOnly && (
              <WrapperContainer>
                <StatusIndicator status={tableDataStatuses[status?.toLowerCase()]} />
              </WrapperContainer>
            )}
          </WrapperContainer>

          {!isSameData && !loading && (
            <IconButton onClick={onUndoChanges}>
              <SVG.UndoArrow />

              <Paragraph2>{t(strCode.UNDO_CHANGES_BTN)}</Paragraph2>
            </IconButton>
          )}
        </WrapperContainer>

        <WrapperContainer Container={FlexContainers.FlexColumnStart} width="100%" height={"100%"}>
          {error ? (
            <WrapperContainer
              Container={FlexContainers.FlexColumnCenter}
              width={"100%"}
              mt={200}
              textAlign={"center"}>
              <Paragraph2>{t(strCode.CONSENT_FETCH_ERROR)}</Paragraph2>
            </WrapperContainer>
          ) : (
            <ConsentElementsTable
              consentElementsData={consentElements ?? []}
              isViewOnly={isViewOnly}
              setConsentElementsData={onUpdatePositions}
            />
          )}
        </WrapperContainer>

        {!loading && (
          <>
            {isViewOnly || !consentElements.length ? (
              <WrapperContainer Container={FlexContainers.FlexRowEnd} gap={16} width={"100%"}>
                <SmallButton onClick={onClose}>{t(strCode.BACK_TO_OVERVIEW_BTN)}</SmallButton>
              </WrapperContainer>
            ) : (
              <WrapperContainer Container={FlexContainers.FlexRowBetween} gap={16} width={"100%"}>
                <WrapperContainer Container={FlexContainers.FlexRowStart} gap={12}>
                  <SmallButton secondary onClick={onBack}>
                    {t(strCode.BACK_BTN)}
                  </SmallButton>

                  {!isSameData && (
                    <SmallButton secondary isRedText onClick={onClose}>
                      {t(strCode.DISCARD_BTN)}
                    </SmallButton>
                  )}
                </WrapperContainer>

                <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16}>
                  {!isSameData && (
                    <SmallButton secondary onClick={onSaveDraft}>
                      {t(strCode.SAVE_DRAFT_BTN)}
                    </SmallButton>
                  )}

                  <SmallButton onClick={() => setActivateModalActive(true)}>
                    {t(strCode.SAVE_AND_ACTIVATE_BTN)}
                  </SmallButton>
                </WrapperContainer>
              </WrapperContainer>
            )}
          </>
        )}
      </WrapperContainer>

      <ChangesNotSavedModal
        setActive={setIsChangesNotSavedModalActive}
        active={isChangesNotSavedModalActive}
        consentVersionTitle={consentVersionTitle}
        onDiscard={onClose}
        onSaveDraft={onSaveDraft}
        onSaveAndContinue={onSaveAndActivate}
      />

      <Modal
        id="activate-modal"
        closable
        title={t(strCode.ACTIVATE_MODAL_TITLE)}
        active={activateModalActive}
        setActive={setActivateModalActive}>
        <WrapperContainer
          Container={FlexContainers.FlexColumn}
          gap={32}
          width={"100%"}
          maxWidth={614}>
          <Paragraph2
            dangerouslySetInnerHTML={{
              __html: version
                ? t(strCode.ACTIVATE_MODAL_TEXT, { version })
                : t(strCode.ACTIVATE_MODAL_THIS_VERSION_TEXT)
            }}
          />

          <WrapperContainer Container={FlexContainers.FlexRowEnd} gap={16} width={"100%"}>
            <SmallButton secondary onClick={onSaveDraft}>
              {t(strCode.SAVE_DRAFT_INSTEAD_BTN)}
            </SmallButton>
            <SmallButton onClick={onSaveAndActivate}>{t(strCode.ACTIVATE_NOW_BTN)}</SmallButton>
          </WrapperContainer>
        </WrapperContainer>
      </Modal>
    </PageContainer>
  );
};

export default withSection(ConsentElementsPage);
